import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  CHANGES_QUEST_ITEM_ID,
  CURVE_QUEST_ITEM_ID,
  DOTS_QUEST_ITEM_ID,
  FLOWERS_QUEST_ITEM_ID,
  VOLUME_QUEST_ITEM_ID,
} from '@shared'
import {
  DualityStage,
  MomentStage,
  QuestId,
  useInventory,
  useMainQuest,
  useMainQuestLoadingState,
  useOverlayActions,
  useShop,
  useShopActions,
} from '@store'

const QUEST_ITEM_BY_SLUG: Partial<Record<QuestId, string>> = {
  [QuestId.Hotey]: FLOWERS_QUEST_ITEM_ID,
  [QuestId.Expansion]: DOTS_QUEST_ITEM_ID,
  [QuestId.Duality]: CHANGES_QUEST_ITEM_ID,
  [QuestId.Void]: VOLUME_QUEST_ITEM_ID,
  [QuestId.Moment]: CURVE_QUEST_ITEM_ID
}
const DUALITY_STAGE_FOR_BLOCK_CHECK = DualityStage.Gate
const MOMENT_STAGE_FOR_BLOCK_CHECK = MomentStage.Lock

export const useQuestBlocked = () => {
  const [shouldByItem, setShouldByItem] = useState(false)
  const overlayTriggered = useRef(false)
  const { questId } = useParams<{ questId: QuestId }>()
  const isLoading = useMainQuestLoadingState()
  const currentStage = useMainQuest.store.currentStage()
  const inventory = useInventory()
  const { setOverlayQueue } = useOverlayActions()
  const questSolved = useMainQuest.store.solved()
  const questItemId = questId ? QUEST_ITEM_BY_SLUG[questId] : null

  const questDataLoaded = !isLoading

  const shouldCheckBlocked =
    questDataLoaded &&
    (questId !== QuestId.Duality ||
      currentStage === DUALITY_STAGE_FOR_BLOCK_CHECK)
      && (
        questId !== QuestId.Moment ||
          currentStage === MOMENT_STAGE_FOR_BLOCK_CHECK
      )

      const shopeItems = useShop((state) => state.items)
  const { getStoreItems } = useShopActions()

  const hasItemInInventory = useMemo(() => {
    return inventory.items.some((card) => card.itemDefId === questItemId)
  }, [inventory])

  const questItem = useMemo(() => {
    if (!questItemId) {
      return null
    }
    return shopeItems?.find((item) => item.id === questItemId)
  }, [shopeItems])

  useEffect(() => {
    if (!shouldCheckBlocked || overlayTriggered.current) return
    if (!questSolved && !hasItemInInventory && questItem) {
      overlayTriggered.current = true
      setShouldByItem(true)
      setOverlayQueue([
        {
          type: 'payment',
          payload: { item: questItem },
        },
      ])
    }
  }, [
    shouldCheckBlocked,
    questSolved,
    hasItemInInventory,
    questItem,
    overlayTriggered.current,
  ])

  useEffect(() => {
    if (!questItemId || hasItemInInventory) {
      return
    }
    getStoreItems()
  }, [hasItemInInventory])

  return {
    questBlocked: shouldCheckBlocked && shouldByItem,
  }
}
